import { Button, Card, ProfileIcon, SendIcon } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { Avatar } from "../../components/Avatar/Avatar";
import { StatusBadge, type StatusKind } from "../../components/StatusBadge/StatusBadge";
import { Page } from "../../components/Page/Page";
import { MergeAccountsModal } from "../../components/Attention/MergeAccountsModal/MergeAccountsModal";
import {
  ATTENTION_KIND_LABEL,
  type AttentionKind,
  type AttentionProblem,
  type AttentionUser,
  type DuplicateGroup,
  computeAttention,
  groupDuplicates,
  isFullySetUp,
} from "../../utils/attention";
import { sendInvitation } from "../../utils/invitation";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";
import styles from "./AttentionPage.module.scss";

// Colocated fragment: same user fields the attention heuristics consume.
const AttentionPage_User = graphql(`
  fragment AttentionPage_User on User {
    uuid
    email
    emailVerified
    emailLower
    newEmail
    impersonateOnly
    invitationOtpValidUntil
    resetPasswordOtpValidUntil
    lastLogin
    passwordChangedAt
    twoFactorMethods {
      uuid
    }
    profiles {
      uuid
      listName
      organization {
        uuid
        name
      }
    }
    profilesConnection {
      totalCount
    }
  }
`);

const AttentionPageQuery = graphql(`
  query AttentionPage_Query {
    users {
      ...AttentionPage_User
    }
  }
`);

const AttentionPage_UpdateUser = graphql(`
  mutation AttentionPage_UpdateUser($userUuid: ID!, $update: UserUpdateInput!) {
    updateUsers(where: { uuid: $userUuid }, update: $update) {
      users {
        uuid
        email
        emailLower
      }
    }
  }
`);

// The two simple, row-based groups (duplicates get their own grouped layout).
const ROW_KINDS: AttentionKind[] = ["loginIssue", "uppercaseEmail"];
const ROW_HINT: Record<string, string> = {
  loginIssue:
    "Ein Konto besteht, aber die E-Mail wurde nie bestätigt und es ist keine Einladung mehr offen. Lösung: Einladung erneut senden.",
  uppercaseEmail:
    "Die E-Mail enthält Großbuchstaben, was je nach Anmeldeweg zu Login-Problemen führen kann. Lösung: klein geschrieben speichern.",
};
const ROW_BADGE: Record<string, { status: StatusKind; label: string }> = {
  loginIssue: { status: "warn", label: "Prüfen" },
  uppercaseEmail: { status: "warn", label: "Prüfen" },
};

function userName(u: AttentionUser): string {
  return u.profiles?.[0]?.listName || u.email || u.uuid;
}

function accountState(u: AttentionUser): string {
  if (isFullySetUp(u)) {
    return "aktiv";
  }
  if (u.invitationOtpValidUntil) {
    return "eingeladen";
  }
  return "unbestätigt";
}

export function AttentionPage() {
  const navigate = useNavigate();
  const [{ data, fetching }] = useQuery({ query: AttentionPageQuery });
  const [, updateUser] = useMutation(AttentionPage_UpdateUser);
  const [busy, setBusy] = useState<string | null>(null);
  const [merging, setMerging] = useState<DuplicateGroup | null>(null);
  // Rows the operator has already resolved this session (optimistic removal, so
  // the list doesn't have to refetch all users).
  const [resolved, setResolved] = useState<Set<string>>(() => new Set());

  const users = getFragmentData(AttentionPage_User, data?.users ?? []);
  const problems = useMemo(() => computeAttention(users), [users]);
  const visible = useMemo(
    () => problems.filter((p) => !resolved.has(p.user.uuid)),
    [problems, resolved],
  );

  const duplicates = useMemo(() => groupDuplicates(visible), [visible]);
  const rowGroups = useMemo(() => {
    const map = new Map<AttentionKind, AttentionProblem[]>();
    for (const p of visible) {
      if (p.kind === "duplicateEmail") {
        continue;
      }
      const arr = map.get(p.kind) ?? [];
      arr.push(p);
      map.set(p.kind, arr);
    }
    return ROW_KINDS.filter((k) => map.has(k)).map((k) => ({ kind: k, list: map.get(k) ?? [] }));
  }, [visible]);

  const openUser = (u: AttentionUser) => {
    const org = u.profiles?.[0]?.organization;
    navigate(org ? `/users/${u.uuid}/orgs/${org.uuid}` : `/users/${u.uuid}`);
  };

  const invite = async (u: AttentionUser) => {
    const profileUuid = u.profiles?.[0]?.uuid;
    if (!profileUuid || !u.email) {
      return;
    }
    setBusy(u.uuid);
    try {
      await sendInvitation(profileUuid, u.email);
      showSuccessToast(`Einladung an ${u.email} gesendet`);
    } catch (error) {
      showUserErrorToast({ text: "Einladung fehlgeschlagen", error });
    } finally {
      setBusy(null);
    }
  };

  const fixLowercase = async (u: AttentionUser) => {
    if (!u.email) {
      return;
    }
    setBusy(u.uuid);
    const res = await updateUser({ userUuid: u.uuid, update: { email: u.email.toLowerCase() } });
    setBusy(null);
    if (res.error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("E-Mail klein geschrieben");
    setResolved((s) => new Set(s).add(u.uuid));
  };

  const empty = visible.length === 0;

  return (
    <Page
      title="Braucht Aufmerksamkeit"
      description="Alle Konten mit Auffälligkeiten — gruppiert nach Problem, mit Lösungsvorschlägen."
    >
      {fetching && problems.length === 0 ? (
        <span className={styles.muted}>Lädt …</span>
      ) : empty ? (
        <Card>
          <span className={styles.muted}>Keine Auffälligkeiten — alle Konten sind in Ordnung.</span>
        </Card>
      ) : (
        <div className={styles.stack}>
          {duplicates.length > 0 && (
            <Card
              header={{ headline: `${ATTENTION_KIND_LABEL.duplicateEmail} · ${duplicates.length}` }}
            >
              <div className={styles.hint}>
                Mehrere Konten teilen sich eine E-Mail — die Anmeldung ist für diese Konten
                blockiert. Ist höchstens ein Konto fertig eingerichtet, lassen sich die übrigen
                darauf zusammenführen; bei mehreren aktiven Konten müssen zuerst die abhängigen
                Produkte geprüft werden.
              </div>
              <div className={styles.dupList}>
                {duplicates.map((g) => (
                  <div key={g.email} className={styles.dupGroup}>
                    <div className={styles.dupHead}>
                      <code className={styles.dupEmail}>{g.email}</code>
                      {g.mergeable ? (
                        <StatusBadge status="info" label="Zusammenführbar" />
                      ) : (
                        <StatusBadge status="warn" label="Manuelle Prüfung" />
                      )}
                      <span className={styles.grow} />
                      {g.mergeable ? (
                        <Button hierarchy="secondary" height="s" onClick={() => setMerging(g)}>
                          Zusammenführen
                        </Button>
                      ) : null}
                    </div>
                    {!g.mergeable && (
                      <div className={styles.dupNote}>
                        Mehrere aktive Konten ({g.readyCount}) — zuerst Produkt-Abhängigkeiten
                        prüfen.
                      </div>
                    )}
                    <div className={styles.dupAccounts}>
                      {g.users.map((u) => (
                        <div key={u.uuid} className={styles.dupAccount}>
                          <span
                            className={`${styles.dot} ${isFullySetUp(u) ? styles.dotReady : styles.dotIdle}`}
                          />
                          <span className={styles.dupName}>{userName(u)}</span>
                          <span className={styles.dupMeta}>
                            {accountState(u)}
                            {u.profiles?.[0]?.organization
                              ? ` · ${u.profiles[0].organization.name}`
                              : ""}
                          </span>
                          <span className={styles.grow} />
                          <Button
                            hierarchy="tertiary"
                            height="s"
                            icon={<ProfileIcon />}
                            ariaLabel={`${userName(u)} öffnen`}
                            onClick={() => openUser(u)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {rowGroups.map(({ kind, list }) => (
            <Card
              key={kind}
              header={{ headline: `${ATTENTION_KIND_LABEL[kind]} · ${list.length}` }}
            >
              <div className={styles.hint}>{ROW_HINT[kind]}</div>
              <div className={styles.list}>
                {list.map((p) => {
                  const u = p.user;
                  const org = u.profiles?.[0]?.organization;
                  const canInvite = Boolean(u.email && u.profiles?.[0]?.uuid);
                  return (
                    <div key={u.uuid} className={styles.row}>
                      <Avatar name={userName(u)} size={34} />
                      <div className={styles.main}>
                        <div className={styles.name}>{userName(u)}</div>
                        <div className={styles.sub}>
                          {u.email || "—"}
                          {org ? ` · ${org.name}` : ""}
                        </div>
                      </div>
                      <StatusBadge status={ROW_BADGE[kind].status} label={ROW_BADGE[kind].label} />
                      <div className={styles.actions}>
                        {kind === "loginIssue" && canInvite && (
                          <Button
                            hierarchy="tertiary"
                            height="s"
                            icon={<SendIcon />}
                            disabled={busy === u.uuid}
                            onClick={() => void invite(u)}
                          >
                            Einladung senden
                          </Button>
                        )}
                        {kind === "uppercaseEmail" && u.email && (
                          <Button
                            hierarchy="tertiary"
                            height="s"
                            disabled={busy === u.uuid}
                            onClick={() => void fixLowercase(u)}
                          >
                            Klein schreiben
                          </Button>
                        )}
                        <Button
                          hierarchy="tertiary"
                          height="s"
                          icon={<ProfileIcon />}
                          ariaLabel={`${userName(u)} öffnen`}
                          onClick={() => openUser(u)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
      {merging && (
        <MergeAccountsModal
          email={merging.email}
          users={merging.users}
          onClose={() => setMerging(null)}
          onMerged={() => {
            // The whole group's duplicate problem is gone once the merge ran —
            // including for the account that was kept.
            setResolved((s) => {
              const next = new Set(s);
              for (const u of merging.users) {
                next.add(u.uuid);
              }
              return next;
            });
            setMerging(null);
          }}
        />
      )}
    </Page>
  );
}
